import { Component, OnInit } from '@angular/core';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  DEFAULT_LIMIT,
  DEFAULT_OFFSET,
} from '@portal-workspace/grow-shared-library';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { PortalHotToastService } from '@portal-workspace/grow-ui-library';
import { setupUntilDestroy } from '@portal-workspace/grow-ui-library';
import { MatSortModule } from '@angular/material/sort';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { createAsyncStore, loadingFor } from '@ngneat/loadoff';
import {
  CustomPaginatorComponent,
  CustomContentLoaderComponent,
} from '@portal-workspace/grow-ui-library';
import { MatTableModule } from '@angular/material/table';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatInputModule } from '@angular/material/input';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgClass, AsyncPipe } from '@angular/common';
import {
  ReconciliationSyncService,
  TransactionReconciliationSync,
} from '../../../service/reconciliation-sync.service';
import moment from 'moment';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardModule } from '@angular/cdk/clipboard';

export class ReconciliationSyncDataSource extends DataSource<TransactionReconciliationSync> {
  subject = new BehaviorSubject<TransactionReconciliationSync[]>([]);

  connect(
    collectionViewer: CollectionViewer
  ): Observable<TransactionReconciliationSync[]> {
    return this.subject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.subject.complete();
  }

  update(originator: TransactionReconciliationSync[]) {
    this.subject.next(originator);
  }
}

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'reconciliation-sync',
  templateUrl: './reconciliation-sync.component.html',
  styleUrls: ['./reconciliation-sync.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    FlexModule,
    MatInputModule,
    NgClass,
    ExtendedModule,
    MatTableModule,
    MatSortModule,
    CustomPaginatorComponent,
    AsyncPipe,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatTooltipModule,
    ClipboardModule,
    FormsModule,
    ReactiveFormsModule,
    CustomContentLoaderComponent,
  ],
  providers: [
    ReconciliationSyncService,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'DD/MM/YYYY',
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
})
export class ReconciliationSyncComponent implements OnInit {
  loader = loadingFor('tableLoading');
  store = createAsyncStore();
  errorTitle = 'Error Occurred!';
  errorMessage = 'Please try again.';
  moment = moment;

  retry() {
    this.reload();
  }

  subscriptions: Subscription[] = [];

  dataSource = new ReconciliationSyncDataSource();

  displayColumns = [
    'date',
    'fileName',
    'status',
    'error',
    'timestamp',
    'actions',
  ];

  total = 0;
  limit = DEFAULT_LIMIT;
  offset = 0;
  startDate: string | null = null;
  endDate: string | null = null;

  formControlStartDate: FormControl<moment.Moment | null> = this.formBuilder.control(null);
  formControlEndDate: FormControl<moment.Moment | null> = this.formBuilder.control(null);

  constructor(
    private formBuilder: FormBuilder,
    private toastService: PortalHotToastService,
    private reconciliationSyncService: ReconciliationSyncService
  ) {
  }

    ngOnInit(): void {
    // Default to last month
    const lastMonth = moment().subtract(1, 'month');
    this.formControlStartDate.setValue(lastMonth);
    this.formControlEndDate.setValue(moment());
    
    setupUntilDestroy(this);
    this.reload();
    
    // Watch for date changes
    const startDateSub = this.formControlStartDate.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        tap((r) => {
          this.startDate = r ? r.format('DD/MM/YYYY') : null;
          this.reset();
          this.reload();
        })
      )
      .subscribe();
    this.subscriptions.push(startDateSub);

    const endDateSub = this.formControlEndDate.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        tap((r) => {
          this.endDate = r ? r.format('DD/MM/YYYY') : null;
          this.reset();
          this.reload();
        })
      )
      .subscribe();
    this.subscriptions.push(endDateSub);
  }

  private reload() {
    this.store = createAsyncStore();
    
    // Convert DD/MM/YYYY to YYYYMMDD for API
    const startDate = this.startDate ? moment(this.startDate, 'DD/MM/YYYY').format('YYYYMMDD') : moment().subtract(1, 'month').format('YYYYMMDD');
    const endDate = this.endDate ? moment(this.endDate, 'DD/MM/YYYY').format('YYYYMMDD') : moment().format('YYYYMMDD');
    
    const sub = this.reconciliationSyncService.getReconciliationSyncRecords(
      this.limit,
      this.offset,
      startDate,
      endDate,
    ).pipe(
      this.loader.tableLoading.track(),
      this.toastService.publishErrorNotificationObservable({
        errorTitle: this.errorTitle,
        errorMessage: this.errorMessage,
        retryFn: this.retry.bind(this),
      }),
      this.store.track(),
        tap((r) => {
          this.total = r.total;
          
          // Check if today is within the filter range
          const today = moment().format('YYYYMMDD');
          const todayFormatted = moment().format('DD/MM/YYYY');
          const startDateMoment = this.startDate ? moment(this.startDate, 'DD/MM/YYYY') : moment().subtract(1, 'month');
          const endDateMoment = this.endDate ? moment(this.endDate, 'DD/MM/YYYY') : moment();
          
          const todayInRange = moment(today, 'YYYYMMDD').isBetween(startDateMoment, endDateMoment, 'day', '[]');
          
          if (todayInRange) {
            // Check if today's record exists in the results
            const todayRecordExists = r.payload.some(record => record.date === today);
            
            if (!todayRecordExists && this.offset === 0) {
              // Add today's record with "Not Attempted" status
              const todayRecord: TransactionReconciliationSync = {
                id: -1, // Use -1 to indicate it's a virtual record
                date: today,
                fileName: null,
                status: 'Not Synced' as any,
                error: null,
                timestamp: null
              };
              
              // Insert today's record at the beginning of the list
              const updatedPayload = [todayRecord, ...r.payload];
              this.total = r.total + 1;
              this.dataSource.update(updatedPayload);
            } else {
              this.dataSource.update(r.payload);
            }
          } else {
            this.dataSource.update(r.payload);
          }
        })
    ).subscribe();
    this.subscriptions.push(sub);
  }

  onPagination($event: PageEvent) {
    this.offset = $event.pageIndex * $event.pageSize;
    this.limit = $event.pageSize;
    this.reload();
  }

  private reset() {
    this.offset = DEFAULT_OFFSET;
  }

  triggerSync(record: TransactionReconciliationSync) {
    this.subscriptions.push(
      this.reconciliationSyncService
        .triggerReconciliationSync(record.date)
        .pipe(
          this.toastService.spinnerObservable(),
          this.toastService.snackBarObservable(
            `Reconciliation sync triggered for ${record.date}`
          )
        )
        .subscribe((r) => {
          this.reload();
        })
    );
  }

  downloadFile(record: TransactionReconciliationSync) {
    this.subscriptions.push(
      this.reconciliationSyncService
        .downloadReconciliationFile(record.date)
        .pipe(
          this.toastService.spinnerObservable(),
          this.toastService.snackBarObservable(
            `Downloading file for ${moment(record.date, 'YYYYMMDD').format('DD/MM/YYYY')}`
          )
        )
        .subscribe((blob: Blob) => {
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Grow_BankRec_${record.date}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          this.toastService.snackBarObservable('File downloaded successfully');
        })
    );
  }

  canTriggerSync(record: TransactionReconciliationSync): boolean {
    return record.status !== 'Successful';
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Successful':
        return 'status-success';
      case 'Failed':
        return 'status-failed';
      case 'Not Synced':
        return 'status-not-attempted';
      case 'Not Found':
        return 'status-not-attempted';
      default:
        return '';
    }
  }

  copyErrorToClipboard(error: string, event: boolean) {
    this.toastService.quickInfoToast('Error copied to clipboard');
  }
}
